import {useState, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {
  hasLoginV2Workflow,
  requiresHcpVerification,
  requiresAuthentication,
  getLoginType,
  isNpiModalType,
  getNextStepAfterVerification,
  shouldShowHcpVerificationOnLogin,
} from './index';
import {getAuthenticated} from 'selectors/login';
import {getSettings} from 'selectors/settings';

/**
 * React hook for authentication flow management
 *
 * Handles:
 * - HCP verification display logic
 * - Authentication state management
 * - Route-based flow control
 *
 * @returns {Object} Authentication state and handlers
 */
export const useAuth = () => {
  const history = useHistory();
  const location = useLocation();
  const settings = useSelector(getSettings);
  const authenticated = useSelector(getAuthenticated);

  // Track the last processed state to prevent duplicate updates
  const lastProcessedStateRef = useRef({pathname: '', authenticated: false});

  const [showHcpVerification, setShowHcpVerification] = useState(false);
  const [hcpVerified, setHcpVerified] = useState(false);

  // Clear modal states when authenticated
  useEffect(() => {
    if (authenticated) {
      setShowHcpVerification(false);
      setHcpVerified(false);
      lastProcessedStateRef.current = {pathname: location.pathname, authenticated: true};
    }
  }, [authenticated, location.pathname]);

  useEffect(() => {
    // Only run this logic for LoginV2 workflow
    if (!hasLoginV2Workflow(settings)) {
      return;
    }

    // Don't show modals if already authenticated
    if (authenticated) {
      setShowHcpVerification(false);
      setHcpVerified(false);
      lastProcessedStateRef.current = {pathname: location.pathname, authenticated};
      return;
    }

    // Check if we've already processed this exact state combination
    // This prevents double rendering in React StrictMode
    const currentState = {pathname: location.pathname, authenticated};
    if (
      lastProcessedStateRef.current.pathname === currentState.pathname &&
      lastProcessedStateRef.current.authenticated === currentState.authenticated
    ) {
      return;
    }

    // Determine if HCP verification should show
    const shouldShow = shouldShowHcpVerificationOnLogin(
      settings,
      authenticated,
      location.pathname
    );

    // Set the state directly to ensure modal shows
    setShowHcpVerification(shouldShow);
    lastProcessedStateRef.current = currentState;

    // Reset hcpVerified when route changes away from /login or /
    if (location.pathname !== '/login' && location.pathname !== '/' && !authenticated) {
      setHcpVerified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, authenticated, settings]);

  // Early return if legacy workflow: same keys as the LoginV2 return below—keep them in
  // sync when adding fields. Expected legacy shape is asserted in
  // tests/services/authentication/useAuth.test.js ("Legacy workflow behavior"). Legacy
  // login UI is PalPage (src/pages/Pal/index.jsx); LoginModals only mounts when loginV2
  // is on (see components/Login/LoginModals.jsx), but callers must still get a complete object.
  if (!hasLoginV2Workflow(settings)) {
    return {
      authenticated,
      hcpVerified: false,
      showHcpVerification: false,
      requiresAuthentication: false,
      requiresHcpVerification: false,
      loginType: '',
      isNpiModalType: false,
      shouldShowLoginInModal: false,
      shouldShowLoginOnPage: false,
      handleHcpVerificationAccept: () => {},
      handleHcpVerificationReject: () => {},
      handleHcpVerificationCancel: () => {},
    };
  }

  /**
   * Handles HCP verification acceptance
   * Determines next step based on configuration
   * Note: Component rendering (LoginModals vs Login) is handled by PalPage based on login type
   */
  const handleHcpVerificationAccept = () => {
    setHcpVerified(true);
    setShowHcpVerification(false);

    const nextStep = getNextStepAfterVerification(settings);

    switch (nextStep) {
      case 'COVERAGE_PAGE':
        history.push('/');
        break;
      case 'LOGIN_MODAL':
        // Stay on current route, LoginModals component will handle showing NPI login modal
        // PalPage renders LoginModals for npiModal type
        break;
      case 'LOGIN_PAGE':
        // Navigate to /login so PalPage renders the full-page Login component for non-modal types
        history.push('/login');
        break;
      default:
        break;
    }
  };

  /**
   * Handles HCP verification rejection
   * Shows redirect modal or exits site
   */
  const handleHcpVerificationReject = () => {
    setShowHcpVerification(false);
    // Redirect logic will be handled by parent component
    // This can trigger redirect modal or exit
  };

  /**
   * Handles cancel from NPI login modal
   * Returns to HCP verification modal
   */
  const handleHcpVerificationCancel = () => {
    setHcpVerified(false);
    // Show HCP verification modal again if it should be shown
    const shouldShow = shouldShowHcpVerificationOnLogin(
      settings,
      authenticated,
      location.pathname
    );
    setShowHcpVerification(shouldShow);
  };

  /**
   * Checks if login form should be shown in modal
   * Shows NPI login modal when:
   * 1. HCP verification is enabled AND user has verified HCP AND authentication is required AND type is npiModal
   * 2. HCP verification is disabled AND authentication is required AND type is npiModal
   */
  const shouldShowLoginInModal = () => {
    // Don't show login modal if already authenticated
    if (authenticated) {
      return false;
    }
    
    // If HCP verification is required, only show after HCP verification and when auth is required
    // (ignore loginV2.type including npiModal when authenticate is false)
    if (requiresHcpVerification(settings)) {
      return hcpVerified && isNpiModalType(settings) && requiresAuthentication(settings);
    }
    
    // If HCP verification is disabled, show NPI modal directly if authentication is required and type is npiModal
    if (!requiresHcpVerification(settings) && requiresAuthentication(settings)) {
      return isNpiModalType(settings);
    }
    
    return false;
  };

  /**
   * Checks if login form should be shown on page
   */
  const shouldShowLoginOnPage = () => {
    if (!hcpVerified && requiresHcpVerification(settings)) {
      return false;
    }

    if (requiresAuthentication(settings)) {
      return !isNpiModalType(settings);
    }

    return false;
  };

  return {
    authenticated,
    hcpVerified,
    showHcpVerification,
    requiresAuthentication: requiresAuthentication(settings),
    requiresHcpVerification: requiresHcpVerification(settings),
    loginType: getLoginType(settings),
    isNpiModalType: isNpiModalType(settings),
    shouldShowLoginInModal: shouldShowLoginInModal(),
    shouldShowLoginOnPage: shouldShowLoginOnPage(),
    handleHcpVerificationAccept,
    handleHcpVerificationReject,
    handleHcpVerificationCancel,
  };
};
